import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import ShareButton from '../components/ShareButton';
import {
  Heart,
  Trash2,
  MapPin,
  Loader2,
  ExternalLink,
  ArrowLeft,
  Scale,
} from 'lucide-react';
import { toast } from 'sonner';
import { translateTemplate } from '../lib/publicListingTruth';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DEMO_KEY = 'tla_demo_favorites';

function readDemoFavorites() {
  try {
    return JSON.parse(localStorage.getItem(DEMO_KEY) || '[]');
  } catch {
    return [];
  }
}

export default function FavoritesPage() {
  const navigate = useNavigate();
  const { user, token, loading: authLoading } = useAuth();
  const { t } = useLanguage();
  const [backendFavorites, setBackendFavorites] = useState([]);
  const [demoFavorites, setDemoFavorites] = useState(readDemoFavorites);
  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState(null);
  const [selectedForCompare, setSelectedForCompare] = useState([]);
  const [compareMode, setCompareMode] = useState(false);

  const favorites = [...backendFavorites, ...demoFavorites];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login?redirect=/favorites');
      return;
    }
    fetchBackendFavorites();
  }, [user, authLoading, navigate]);

  const fetchBackendFavorites = async () => {
    try {
      const response = await fetch(`${API_URL}/api/favorites`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const raw = Array.isArray(data) ? data : (data.favorites || []);
        setBackendFavorites(raw.map(f => ({
          favorite_id: f.favorite_id || f.id,
          listing_id: f.listing?.id || f.listing_id,
          listing_title: f.listing?.title || f.listing_title,
          listing_photo: f.listing?.hero_photo_url || f.listing_photo,
          listing_location: f.listing?.location_name || f.listing_location,
          listing_price: f.listing?.base_amount || f.listing_price,
          created_at: f.created_at,
          is_demo: false,
        })));
      }
    } catch (error) {
      console.error('Failed to fetch favorites:', error);
      toast.error(t('favorites.load_failed', 'Failed to load favorites'));
    } finally {
      setLoading(false);
    }
  };

  const removeFavorite = async (fav) => {
    setRemoving(fav.listing_id);
    try {
      if (fav.is_demo) {
        const updated = readDemoFavorites().filter(f => f.listing_id !== fav.listing_id);
        localStorage.setItem(DEMO_KEY, JSON.stringify(updated));
        setDemoFavorites(updated);
        setSelectedForCompare(prev => prev.filter(id => id !== fav.listing_id));
        window.dispatchEvent(new Event('favorites-updated'));
        toast.success(t('favorites.demo_removed', 'Removed preview property'));
      } else {
        const response = await fetch(`${API_URL}/api/favorites/${fav.listing_id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          setBackendFavorites(prev => prev.filter(f => f.listing_id !== fav.listing_id));
          setSelectedForCompare(prev => prev.filter(id => id !== fav.listing_id));
          window.dispatchEvent(new Event('favorites-updated'));
          toast.success(t('favorites.removed', 'Removed from favorites'));
        } else {
          toast.error(t('favorites.remove_error', 'Failed to remove favorite'));
        }
      }
    } catch (error) {
      toast.error(t('favorites.remove_error', 'Failed to remove favorite'));
    } finally {
      setRemoving(null);
    }
  };

  const toggleCompareSelection = (listingId) => {
    setSelectedForCompare(prev => {
      if (prev.includes(listingId)) return prev.filter(id => id !== listingId);
      if (prev.length >= 4) {
        toast.info(t('favorites.compare_max', 'Maximum 4 properties can be compared'));
        return prev;
      }
      return [...prev, listingId];
    });
  };

  const handleCompare = () => {
    if (selectedForCompare.length < 2) {
      toast.error(t('favorites.compare_min', 'Select at least 2 properties to compare'));
      return;
    }
    navigate(`/compare?ids=${selectedForCompare.join(',')}`);
  };

  const removeAll = async () => {
    if (!window.confirm(t('favorites.confirm_remove_all', 'Remove all favorites? This cannot be undone.'))) return;
    // Remove backend favorites
    for (const fav of backendFavorites) {
      await fetch(`${API_URL}/api/favorites/${fav.listing_id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    }
    // Remove demo favorites
    localStorage.setItem(DEMO_KEY, '[]');
    setBackendFavorites([]);
    setDemoFavorites([]);
    window.dispatchEvent(new Event('favorites-updated'));
    toast.success(t('favorites.all_removed', 'All favorites removed'));
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-slate-100 pt-20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-signal-blue animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 pt-20" data-testid="favorites-page">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 text-slate-600 hover:text-slate-200 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="font-heading text-3xl font-bold text-slate-800 flex items-center gap-3">
                <Heart className="w-8 h-8 text-red-500 fill-current" />
                {t('favorites.title', 'MY FAVORITES').toUpperCase()}
              </h1>
              <p className="text-slate-600">
                {favorites.length} {t('favorites.saved_count', 'saved')} {favorites.length === 1 ? t('favorites.property', 'property') : t('favorites.properties', 'properties')}
              </p>
            </div>
          </div>

          {favorites.length > 1 && (
            <div className="flex items-center gap-2">
              <Button
                onClick={() => setCompareMode(!compareMode)}
                variant={compareMode ? 'default' : 'outline'}
                className={compareMode
                  ? 'bg-signal-blue text-white'
                  : 'border-slate-300 text-slate-700 hover:bg-slate-100'
                }
              >
                <Scale className="w-4 h-4 mr-2" />
                {compareMode ? t('common.cancel', 'Cancel') : t('compare.compare', 'Compare')}
              </Button>
              {compareMode && selectedForCompare.length >= 2 && (
                <Button
                  onClick={handleCompare}
                  className="bg-military-gold text-slate-950 hover:bg-military-gold/90"
                >
                  {t('compare.compare', 'Compare')} {selectedForCompare.length}
                </Button>
              )}
            </div>
          )}

          {favorites.length > 0 && !compareMode && (
            <div className="flex items-center gap-3">
              <ShareButton
                url={window.location.href}
                title={t('favorites.share.title', 'My Saved Properties - TLA Housing Europe')}
                description={translateTemplate(
                  t(
                    'favorites.share.description',
                    'Check out my {count} saved TLA-compliant properties!',
                  ),
                  { count: favorites.length },
                )}
                variant="outline"
              />
              <Button
                variant="outline"
                onClick={removeAll}
                className="border-red-500/30 text-red-400 hover:bg-red-500/10"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {t('favorites.clear_all', 'Clear All')}
              </Button>
            </div>
          )}
        </div>

        {/* Favorites Grid */}
        {favorites.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
            <Heart className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <h2 className="font-heading text-2xl font-bold text-slate-800 mb-2">
              {t('favorites.no_favorites', 'No Saved Properties Yet')}
            </h2>
            <p className="text-slate-600 mb-6 max-w-md mx-auto">
              {t('favorites.start_exploring', "Start exploring and save properties you love. They'll appear here so you can easily compare and share them.")}
            </p>
            <Button
              onClick={() => navigate('/listings')}
              className="bg-signal-blue hover:bg-signal-blue-hover text-white"
            >
              {t('favorites.browse_listings', 'Browse Properties')}
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {favorites.map((fav) => (
              <div
                key={fav.favorite_id || fav.listing_id}
                className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden group hover:border-signal-blue/30 transition-colors"
              >
                {/* Image */}
                <Link to={`/listings/${fav.listing_id}`} className="block">
                  <div className="relative aspect-[4/3] overflow-hidden bg-slate-800">
                    {fav.listing_photo ? (
                      <img
                        src={fav.listing_photo}
                        alt={fav.listing_title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Heart className="w-12 h-12 text-slate-600" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 to-transparent" />
                    {fav.is_demo && (
                      <div className="absolute top-2 left-2">
                        <span className="text-[10px] uppercase tracking-wider font-bold text-amber-400 bg-amber-400/20 border border-amber-400/30 px-2 py-0.5 rounded">
                          {t('favorites.preview_badge', 'Preview')}
                        </span>
                      </div>
                    )}
                    {fav.listing_price > 0 && (
                      <div className="absolute bottom-3 left-3">
                        <div className="bg-slate-100/90 backdrop-blur-sm px-3 py-1.5 rounded-sm">
                          <span className="font-heading text-xl font-bold text-slate-800">
                            €{fav.listing_price.toLocaleString()}
                          </span>
                          <span className="text-slate-600 text-sm">/mo</span>
                        </div>
                      </div>
                    )}
                  </div>
                </Link>

                {/* Content */}
                <div className="p-4">
                  <Link to={`/listings/${fav.listing_id}`}>
                    <h3 className="font-heading text-lg font-bold text-slate-800 mb-1 line-clamp-1 group-hover:text-signal-blue transition-colors">
                      {fav.listing_title}
                    </h3>
                  </Link>
                  <div className="flex items-center gap-1 text-slate-600 text-sm mb-3">
                    <MapPin className="w-3.5 h-3.5" />
                    <span>{fav.listing_location || 'Europe'}</span>
                  </div>
                  <div className="flex items-center justify-between pt-3 border-t border-slate-200">
                    <p className="text-xs text-slate-500">
                      {t('favorites.saved_on', 'Saved')} {new Date(fav.created_at).toLocaleDateString()}
                    </p>
                    <div className="flex items-center gap-2">
                      <Link
                        to={`/listings/${fav.listing_id}`}
                        className="p-2 text-slate-600 hover:text-signal-blue transition-colors"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Link>
                      <button
                        onClick={() => removeFavorite(fav)}
                        disabled={removing === fav.listing_id}
                        className="p-2 text-slate-600 hover:text-red-400 transition-colors"
                      >
                        {removing === fav.listing_id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
